import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_MY_VOTING_ARTICLES, UPDATE_VOTING_ARTICLE } from '../graphql/voting';
import { formatDate } from '../utils/dateUtils';

const MyVoting = () => {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    status: 'PUBLISHED',
    allowComments: true,
    showVoteResults: true
  });

  const { loading, error, data, refetch } = useQuery(GET_MY_VOTING_ARTICLES, {
    fetchPolicy: 'cache-and-network'
  });

  const [updateVotingArticle] = useMutation(UPDATE_VOTING_ARTICLE, {
    onCompleted: (data) => {
      if (data.updateVotingArticle.success) {
        alert('更新成功！');
        setEditingId(null);
        refetch();
      } else {
        alert('更新失敗：' + data.updateVotingArticle.message);
      }
    },
    onError: (error) => {
      console.error('更新失敗:', error);
      alert('更新失敗：' + error.message);
    }
  });



  const handleEdit = (article) => {
    setEditingId(article.id);
    setEditForm({
      status: article.status,
      allowComments: article.allowComments,
      showVoteResults: article.showVoteResults
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      status: 'PUBLISHED',
      allowComments: true,
      showVoteResults: true
    });
  };

  const handleSaveEdit = async () => {
    try {
      await updateVotingArticle({
        variables: {
          input: {
            id: editingId,
            status: editForm.status,
            allowComments: editForm.allowComments,
            showVoteResults: editForm.showVoteResults
          }
        }
      });
    } catch (error) {
      console.error('保存失敗:', error);
    }
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // 將字符串 "true"/"false" 轉換為布爾值
    let processedValue = value;
    if (value === 'true') processedValue = true;
    if (value === 'false') processedValue = false;
    
    setEditForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const articles = data?.myVotingArticles?.articles || [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">載入失敗</div>
          <button 
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-6xl mx-auto">
        {/* 操作按鈕 */}
        <div className="mb-6 flex justify-end">
          <button 
            onClick={() => navigate('/voting/favorites')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            💜 我的收藏
          </button>
        </div>

        {/* 統計資訊 */}
        <div className="mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {articles.length}
                </div>
                <div className="text-sm text-gray-600">總投票數</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {articles.filter(a => a.status === 'PUBLISHED').length}
                </div>
                <div className="text-sm text-gray-600">進行中</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {articles.reduce((sum, a) => sum + (a.voteCount || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">總參與人數</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {articles.reduce((sum, a) => sum + (a.commentCount || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">總評論數</div>
              </div>
            </div>
          </div>
        </div>

        {/* 投票列表 */}
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">你還沒有創建任何投票</div>
            <div className="text-gray-400 text-sm mt-2">點擊頂部的「創建投票」按鈕開始</div>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <div key={article.id} className="bg-white rounded-lg shadow-sm border p-6">
                {editingId === article.id ? (
                  // 編輯模式
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        狀態
                      </label>
                      <select
                        name="status"
                        value={editForm.status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="PUBLISHED">進行中</option>
                        <option value="CLOSED">已結束</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        允許評論
                      </label>
                      <select
                        name="allowComments"
                        value={editForm.allowComments.toString()}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="true">是</option>
                        <option value="false">否</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        顯示投票結果
                      </label>
                      <select
                        name="showVoteResults"
                        value={editForm.showVoteResults.toString()}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="true">是</option>
                        <option value="false">否</option>
                      </select>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  // 顯示模式
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {article.question?.title || '未知問題'}
                        </h3>
                        <p className="text-gray-600 mb-3">
                          {article.question?.description || ''}
                        </p>
                        <div className="flex items-center text-sm text-gray-500 space-x-4">
                          <span>🆔 {article.id}</span>
                          <span>📅 {formatDate(article.createdAt)}</span>
                          <span>🗳️ {article.voteCount || 0} 票</span>
                          <span>💬 {article.commentCount || 0} 評論</span>
                          <span>👁️ {article.viewCount || 0} 瀏覽</span>
                        </div>
                      </div>
                      <div className="ml-4 flex flex-col items-end space-y-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          article.status === 'PUBLISHED' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {article.status === 'PUBLISHED' ? '進行中' : '已結束'}
                        </span>
                        {article.isAnonymous && (
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                            🎭 匿名發布
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/voting/${article.id}`)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          查看詳情
                        </button>
                        <button
                          onClick={() => handleEdit(article)}
                          className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                        >
                          編輯
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVoting; 